// src/components/explorers/wildfireExplorer/tabs/PerimetersTab.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { getCurrentPerimeter, getHistoricPerimeters, deletePerimeter } from '../../../../services/perimeterApiService';
import { getShownOverlayKeys } from '../../../../easymap/perimeters/firePerimeterOverlay';
import type { PerimeterRecord, HistoricPerimeterRecord } from '../../../../types/perimeter';
import { PerimeterUploadModal } from './PerimeterUploadModal';
import { formatPerimeterSource } from '../../../../constants/perimeterSource';
import { usePermissions } from '../../../../context/PermissionContext';
import { getCmsUrl } from '../../../../services/configService';
import AppModal from '../../../common/AppModal';
import ActionDropdown from '../../../common/ActionDropdown';
import './PerimetersTab.scss';

function nodeDownloadUrl(cmsNodeId: number | null | undefined): string | null {
  if (cmsNodeId == null) return null;
  const cms = (getCmsUrl() || '').replace(/\/+$/, '');
  return cms ? `${cms}/node/${cmsNodeId}` : null;
}

/**
 * Track which perimeters are currently drawn on the map. Driven by the
 * fire-perimeter-shown / fire-perimeter-hidden events that
 * firePerimeterOverlay dispatches. Lets us flip button labels reactively
 * without having to query mapbox layer state.
 *
 * On mount we seed from getShownOverlayKeys() so closing+reopening the
 * detail modal doesn't desync the labels from the (still-drawn) overlays.
 */
function useShownPerimeters(): { has(key: string): boolean } {
  const [shown, setShown] = useState<Set<string>>(() => new Set(getShownOverlayKeys()));
  useEffect(() => {
    const onShown = (ev: Event) => {
      const d = (ev as CustomEvent<{ fireNumber: string; perimeterId?: string }>).detail;
      const key = d.perimeterId ?? `current:${d.fireNumber}`;
      setShown((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
    };
    const onHidden = (ev: Event) => {
      const d = (ev as CustomEvent<{ fireNumber: string; perimeterId?: string }>).detail;
      const key = d.perimeterId ?? `current:${d.fireNumber}`;
      setShown((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    };
    window.addEventListener('fire-perimeter-shown', onShown);
    window.addEventListener('fire-perimeter-hidden', onHidden);
    return () => {
      window.removeEventListener('fire-perimeter-shown', onShown);
      window.removeEventListener('fire-perimeter-hidden', onHidden);
    };
  }, []);
  return { has: (key: string) => shown.has(key) };
}

interface PerimetersTabProps {
  fireNumber: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatArea(area_ha: number): string {
  return area_ha.toLocaleString(undefined, { maximumFractionDigits: 1 }) + ' ha';
}

function formatDate(isoString: string): string {
  try {
    return new Date(isoString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return isoString;
  }
}

// ── Current Perimeter Card ────────────────────────────────────────────────────

interface CurrentPerimeterCardProps {
  perimeter: PerimeterRecord;
  fireNumber: string;
  isShown: boolean;
  canDelete: boolean;
  onDownload: () => void;
  onToggleMap: () => void;
  onDelete: () => void;
}

const CurrentPerimeterCard: React.FC<CurrentPerimeterCardProps> = ({ perimeter, isShown, canDelete, onDownload, onToggleMap, onDelete }) => {
  const fileUrl = nodeDownloadUrl(perimeter.cms_node_id);
  const dropdownItems = [
    { label: 'Download GeoJSON', onClick: onDownload },
    ...(fileUrl
      ? [{ label: 'Download Shapefile', href: fileUrl, target: '_blank', rel: 'noopener noreferrer' }]
      : []),
    ...(canDelete
      ? [{ label: 'Delete', onClick: onDelete, danger: true }]
      : []),
  ];
  return (
    <>
      <div className="detail-grid">
        <div className="detail-item">
          <label>Area</label>
          <span>{formatArea(perimeter.area_ha)}</span>
        </div>
        <div className="detail-item">
          <label>Uploaded by</label>
          <span>{perimeter.uploaded_by}</span>
        </div>
        <div className="detail-item">
          <label>Uploaded on</label>
          <span>{formatDate(perimeter.uploaded_on)}</span>
        </div>
        <div className="detail-item">
          <label>Format</label>
          <span><span className="format-badge">{perimeter.file_type}</span></span>
        </div>
        {perimeter.description && (
          <div className="detail-item">
            <label>Description</label>
            <span>{perimeter.description}</span>
          </div>
        )}
        {perimeter.source_name && (
          <div className="detail-item">
            <label>Source</label>
            <span>{formatPerimeterSource(perimeter.source_name)}</span>
          </div>
        )}
      </div>
      <div className="perimeter-card__actions">
        <ActionDropdown
          size="sm"
          primary={{
            label: isShown ? 'Hide on Map' : 'Show on Map',
            onClick: onToggleMap,
            active: isShown,
          }}
          items={dropdownItems}
        />
      </div>
    </>
  );
};

// ── Historic Perimeters List ──────────────────────────────────────────────────

interface HistoricListProps {
  historics: HistoricPerimeterRecord[];
  fireNumber: string;
  canDelete: boolean;
  isShownById: (id: string) => boolean;
  onToggleMap: (h: HistoricPerimeterRecord) => void;
  onDownload: (h: HistoricPerimeterRecord) => void;
  onDelete: (h: HistoricPerimeterRecord) => void;
}

const HistoricPerimetersList: React.FC<HistoricListProps> = ({ historics, canDelete, isShownById, onToggleMap, onDownload, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  if (!historics || historics.length === 0) return null;

  // Bulk toggle reflects "is at least one historic currently drawn?". When
  // anyShown is true, the button flips to Hide All + active treatment;
  // clicking hides only the ones that are currently shown. When all are
  // hidden, clicking shows the rest.
  const anyShown = historics.some((h) => isShownById(h.id));
  const handleToggleAll = () => {
    if (anyShown) {
      historics.forEach((h) => {
        if (isShownById(h.id)) onToggleMap(h);
      });
    } else {
      historics.forEach((h) => {
        if (!isShownById(h.id)) onToggleMap(h);
      });
    }
  };

  return (
    <>
      <button
        type="button"
        className="perimeter-section-toggle"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <span>Historic Perimeters ({historics.length})</span>
        <span className="perimeter-section-toggle__arrow">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="perimeter-history-scroll">
          <table className="perimeter-history-table">
          <thead>
            <tr>
              <th className="perimeter-history-table__actions">
                <div className="perimeter-history-table__actions-header">
                  <span>Actions</span>
                  <button
                    type="button"
                    className={`btn btn-sm btn-outline action-dropdown__primary${anyShown ? ' action-dropdown__primary--active' : ''}`}
                    onClick={handleToggleAll}
                    aria-pressed={anyShown}
                  >
                    {anyShown ? 'Hide All' : 'Show All'}
                  </button>
                </div>
              </th>
              <th>Uploaded</th>
              <th>Area</th>
              <th>Uploaded by</th>
              <th>Format</th>
              <th className="perimeter-history-table__description">Description</th>
            </tr>
          </thead>
          <tbody>
            {historics.map((h) => {
              const fileUrl = nodeDownloadUrl(h.cms_node_id);
              const isShown = isShownById(h.id);
              const dropdownItems = [
                { label: 'Download GeoJSON', onClick: () => onDownload(h) },
                ...(fileUrl
                  ? [{ label: 'Download Shapefile', href: fileUrl, target: '_blank', rel: 'noopener noreferrer' }]
                  : []),
                ...(canDelete
                  ? [{ label: 'Delete', onClick: () => onDelete(h), danger: true }]
                  : []),
              ];
              return (
                <tr key={h.id}>
                  <td className="perimeter-history-table__actions">
                    <ActionDropdown
                      size="sm"
                      primary={{
                        label: isShown ? 'Hide on Map' : 'Show on Map',
                        onClick: () => onToggleMap(h),
                        active: isShown,
                      }}
                      items={dropdownItems}
                    />
                  </td>
                  <td>{formatDate(h.uploaded_on)}</td>
                  <td>{formatArea(h.area_ha)}</td>
                  <td>{h.uploaded_by}</td>
                  <td><span className="format-badge">{h.file_type}</span></td>
                  <td className="perimeter-history-table__description">{h.description ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
          </table>
        </div>
      )}
    </>
  );
};

// ── Main PerimetersTab ────────────────────────────────────────────────────────

export const PerimetersTab: React.FC<PerimetersTabProps> = ({ fireNumber }) => {
  const [current, setCurrent] = useState<PerimeterRecord | null>(null);
  const [historics, setHistorics] = useState<HistoricPerimeterRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const shown = useShownPerimeters();
  const { canDo } = usePermissions();
  const canUploadPerimeter = canDo('wildfires', 'upload_perimeter');
  const canDeletePerimeter = canDo('wildfires', 'delete_perimeter');
  const [pendingDelete, setPendingDelete] = useState<{ id: string; label: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const toggleCurrentOnMap = useCallback(() => {
    if (!current) return;
    window.dispatchEvent(
      new CustomEvent('toggle-fire-perimeter', {
        detail: {
          fireNumber,
          geometry: current.geometry,
          flyTo: false,
          label: `${fireNumber} · current (${formatDate(current.uploaded_on)})`,
          description: `${formatArea(current.area_ha)} · ${current.file_type}` + (current.source_name ? ` · ${formatPerimeterSource(current.source_name)}` : ''),
        },
      }),
    );
  }, [current, fireNumber]);

  const toggleHistoricOnMap = useCallback((h: HistoricPerimeterRecord) => {
    window.dispatchEvent(
      new CustomEvent('toggle-fire-perimeter', {
        detail: {
          fireNumber,
          perimeterId: h.id,
          geometry: h.geometry,
          flyTo: false,
          label: `${fireNumber} · uploaded ${formatDate(h.uploaded_on)}`,
          description: `${formatArea(h.area_ha)} · ${h.file_type}` + (h.source_name ? ` · ${formatPerimeterSource(h.source_name)}` : ''),
        },
      }),
    );
  }, [fireNumber]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [currentData, historicData] = await Promise.all([
        getCurrentPerimeter(fireNumber),
        getHistoricPerimeters(fireNumber),
      ]);
      setCurrent(currentData);
      setHistorics(historicData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load perimeters');
    } finally {
      setLoading(false);
    }
  }, [fireNumber]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const downloadGeometryAsGeoJson = useCallback((geometry: GeoJSON.MultiPolygon, suffix: string) => {
    const blob = new Blob([JSON.stringify(geometry, null, 2)], {
      type: 'application/geo+json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fireNumber}_perimeter${suffix}.geojson`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [fireNumber]);

  const handleDownload = useCallback(() => {
    if (!current) return;
    downloadGeometryAsGeoJson(current.geometry, '');
  }, [current, downloadGeometryAsGeoJson]);

  const handleDownloadHistoric = useCallback((h: HistoricPerimeterRecord) => {
    const suffix = `_${(h.uploaded_on || '').slice(0, 10) || h.id.slice(0, 8)}`;
    downloadGeometryAsGeoJson(h.geometry, suffix);
  }, [downloadGeometryAsGeoJson]);

  const handleUploadSuccess = () => {
    setIsUploadOpen(false);
    loadData();
  };

  const requestDeleteCurrent = useCallback(() => {
    if (!current) return;
    setDeleteError(null);
    setPendingDelete({ id: current.id, label: `the current perimeter for ${fireNumber}` });
  }, [current, fireNumber]);

  const requestDeleteHistoric = useCallback((h: HistoricPerimeterRecord) => {
    setDeleteError(null);
    setPendingDelete({ id: h.id, label: `the historic perimeter uploaded on ${formatDate(h.uploaded_on)}` });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deletePerimeter(pendingDelete.id);
      setPendingDelete(null);
      await loadData();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, loadData]);

  return (
    <div className="wildfire-detail-content perimeters-tab">
      <section className="detail-section">
        <div className="section-header">
          <h3>Current Perimeter</h3>
          {canUploadPerimeter && (
            <button
              className="btn btn-primary"
              onClick={() => setIsUploadOpen(true)}
            >
              Upload New Perimeter
            </button>
          )}
        </div>

        {loading && <p>Loading perimeters...</p>}
        {error && <p className="files-warning">Error: {error}</p>}

        {!loading && !error && current === null && (
          <p className="perimeter-empty">No perimeter on file</p>
        )}

        {!loading && !error && current !== null && (
          <CurrentPerimeterCard
            perimeter={current}
            fireNumber={fireNumber}
            isShown={shown.has(`current:${fireNumber}`)}
            canDelete={canDeletePerimeter}
            onDownload={handleDownload}
            onToggleMap={toggleCurrentOnMap}
            onDelete={requestDeleteCurrent}
          />
        )}
      </section>

      {!loading && !error && (
        <section className="detail-section">
          <HistoricPerimetersList
            historics={historics}
            fireNumber={fireNumber}
            canDelete={canDeletePerimeter}
            isShownById={(id) => shown.has(id)}
            onToggleMap={toggleHistoricOnMap}
            onDownload={handleDownloadHistoric}
            onDelete={requestDeleteHistoric}
          />
        </section>
      )}

      <PerimeterUploadModal
        isOpen={isUploadOpen}
        fireNumber={fireNumber}
        onSuccess={handleUploadSuccess}
        onClose={() => setIsUploadOpen(false)}
      />

      <AppModal
        isOpen={pendingDelete !== null}
        onClose={() => { if (!deleting) setPendingDelete(null); }}
        width={460}
        persistent={deleting}
      >
        <div className="perimeter-delete-modal">
          <h3>Delete perimeter?</h3>
          <p>
            You are about to permanently delete {pendingDelete?.label}. The linked file in the CMS will also be removed. This cannot be undone.
          </p>
          {deleteError && <p className="form-error">{deleteError}</p>}
          <div className="form-actions btn-row">
            <button
              type="button"
              className="btn btn-danger"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => setPendingDelete(null)}
              disabled={deleting}
            >
              Cancel
            </button>
          </div>
        </div>
      </AppModal>
    </div>
  );
};
